import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/shared/i18n/useT';
import { useOrg, isAdmin } from '@/shared/org';
import { useDropdownKeyboard } from '@/shared/components/Grid/dropdownKeyboard';
import { ResizeCorner, useResizableSize } from '@/shared/components/Grid/ResizeCorner';
import { ChipListEditor } from '@/shared/components/ui/ChipListEditor';

export interface ItemChip {
  clave: string; // identificador estable: usado como `value`/`onChange` y target de guardados
  texto: string; // etiqueta editable, mostrada en el chip
  color: string | null;
  orden: number;
}

export type ChipItem = ItemChip;

export interface FuenteChips {
  cargar: () => Promise<ItemChip[]>;
  guardarColor: (clave: string, color: string | null) => Promise<string | null>;
  /** Si se omite, el texto no es editable. Devuelve la `clave` vigente tras guardar. */
  guardarTexto?: ((clave: string, texto: string) => Promise<{ error: string | null; nuevaClave: string }>) | undefined;
  /** Si se omite, no se ofrece agregar valores nuevos desde este selector. */
  agregar?: ((texto: string) => Promise<string | null>) | undefined;
  /** Si se omite, no se ofrece eliminar valores desde este selector. */
  eliminar?: ((clave: string) => Promise<string | null>) | undefined;
  /** Si se omite, no se puede reordenar por arrastre. Recibe todas las claves en el nuevo orden. */
  reordenar?: ((clavesEnOrden: string[]) => Promise<string | null>) | undefined;
}

export type ChipSource = FuenteChips;

export interface FuenteOptions {
  orgId?: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dynamicDb = supabase as any;

/** Fuente para valores de `listas` (tipificación, estado, etc.): `valor` es a la vez identidad y etiqueta. */
export function fuenteListas(lista: string, options?: FuenteOptions): FuenteChips {
  return {
    async cargar() {
      let query = dynamicDb.from('listas').select('valor,orden,activo,color').eq('lista', lista).eq('activo', true).order('orden');
      if (options?.orgId) {
        query = query.eq('org_id', options.orgId);
      }
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return (data ?? []).map((f: { valor: string; color: string | null; orden: number }) => ({
        clave: f.valor,
        texto: f.valor,
        color: f.color,
        orden: f.orden,
      }));
    },
    async guardarColor(clave, color) {
      let query = dynamicDb.from('listas').update({ color }).eq('lista', lista).eq('valor', clave);
      if (options?.orgId) {
        query = query.eq('org_id', options.orgId);
      }
      const { error } = await query;
      return error?.message ?? null;
    },
    async guardarTexto(clave, texto) {
      let query = dynamicDb.from('listas').update({ valor: texto }).eq('lista', lista).eq('valor', clave);
      if (options?.orgId) {
        query = query.eq('org_id', options.orgId);
      }
      const { error } = await query;
      return { error: error?.message ?? null, nuevaClave: texto };
    },
    async agregar(texto) {
      let maxQuery = dynamicDb.from('listas').select('orden').eq('lista', lista).order('orden', { ascending: false }).limit(1);
      if (options?.orgId) {
        maxQuery = maxQuery.eq('org_id', options.orgId);
      }
      const { data } = await maxQuery;
      const siguienteOrden = (data?.[0]?.orden ?? 0) + 1;

      const row: { lista: string; valor: string; orden: number; org_id?: string } = {
        lista,
        valor: texto,
        orden: siguienteOrden,
      };
      if (options?.orgId) {
        row.org_id = options.orgId;
      }
      const { error } = await dynamicDb.from('listas').insert(row);
      return error?.message ?? null;
    },
    async eliminar(clave) {
      let query = dynamicDb.from('listas').delete().eq('lista', lista).eq('valor', clave);
      if (options?.orgId) {
        query = query.eq('org_id', options.orgId);
      }
      const { error } = await query;
      return error?.message ?? null;
    },
    async reordenar(clavesEnOrden) {
      const resultados = await Promise.all(
        clavesEnOrden.map((clave, i) => {
          let query = dynamicDb.from('listas').update({ orden: i + 1 }).eq('lista', lista).eq('valor', clave);
          if (options?.orgId) {
            query = query.eq('org_id', options.orgId);
          }
          return query;
        }),
      );
      return resultados.find((r: { error?: { message: string } | null }) => r?.error)?.error?.message ?? null;
    },
  };
}

/** Fuente para `opciones_pago`: `clave` es estable, solo `nombre` (etiqueta) y `color` son editables acá. */
export function fuenteOpcionesPago(options?: FuenteOptions): FuenteChips {
  return {
    async cargar() {
      let query = dynamicDb.from('opciones_pago').select('clave,nombre,orden,color').eq('activa', true).order('orden');
      if (options?.orgId) {
        query = query.eq('org_id', options.orgId);
      }
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return (data ?? []).map((f: { clave: string; nombre: string; color: string | null; orden: number }) => ({
        clave: f.clave,
        texto: f.nombre,
        color: f.color,
        orden: f.orden,
      }));
    },
    async guardarColor(clave, color) {
      let query = dynamicDb.from('opciones_pago').update({ color }).eq('clave', clave);
      if (options?.orgId) {
        query = query.eq('org_id', options.orgId);
      }
      const { error } = await query;
      return error?.message ?? null;
    },
    async guardarTexto(clave, texto) {
      let query = dynamicDb.from('opciones_pago').update({ nombre: texto }).eq('clave', clave);
      if (options?.orgId) {
        query = query.eq('org_id', options.orgId);
      }
      const { error } = await query;
      return { error: error?.message ?? null, nuevaClave: clave };
    },
    async eliminar(clave) {
      let query = dynamicDb.from('opciones_pago').update({ activa: false }).eq('clave', clave);
      if (options?.orgId) {
        query = query.eq('org_id', options.orgId);
      }
      const { error } = await query;
      return error?.message ?? null;
    },
    async reordenar(clavesEnOrden) {
      const resultados = await Promise.all(
        clavesEnOrden.map((clave, i) => {
          let query = dynamicDb.from('opciones_pago').update({ orden: i + 1 }).eq('clave', clave);
          if (options?.orgId) {
            query = query.eq('org_id', options.orgId);
          }
          return query;
        }),
      );
      return resultados.find((r: { error?: { message: string } | null }) => r?.error)?.error?.message ?? null;
    },
  };
}

/** Nombres completos de los códigos fijos de `canal_primer_contacto` (definidos por el negocio). */
export const NOMBRES_CANAL_CONTACTO: Record<string, string> = {
  FACE: 'Facebook',
  FRV: 'Fravega',
  GOO: 'Google',
  IG: 'Instagram',
  MLI: 'Mercado Libre',
  RCL: 'Referido Cliente',
  REM: 'Referido Interno',
  REV: 'Reventa',
  SCO: 'Segunda compra',
  WEB: 'Página Web',
};

export const etiquetaCanalContacto = (clave: string): string =>
  NOMBRES_CANAL_CONTACTO[clave] ? `${clave} (${NOMBRES_CANAL_CONTACTO[clave]})` : clave;

export const codigoCanalContacto = (valor: string): string =>
  valor.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();

/** Fuente para `canal_primer_contacto`: reusa `listas` pero con etiqueta completa y sin edición de texto/orden/alta/baja. */
export function fuenteCanalContacto(options?: FuenteOptions): FuenteChips {
  const base = fuenteListas('canal_primer_contacto', options);
  return {
    ...base,
    async cargar() {
      const items = await base.cargar();
      return items.filter((i) => NOMBRES_CANAL_CONTACTO[i.clave]).map((i) => ({ ...i, texto: etiquetaCanalContacto(i.clave) }));
    },
    guardarTexto: undefined,
    agregar: undefined,
    eliminar: undefined,
    reordenar: undefined,
  };
}

/** Estilo "smart chip" de Google Sheets: fondo y texto derivados del color guardado, mezclados con el tema activo. */
export function estiloChip(color: string | null): CSSProperties {
  if (!color) return {};
  return {
    backgroundColor: `color-mix(in srgb, ${color} 28%, var(--surface))`,
    color: `color-mix(in srgb, ${color} 72%, var(--text))`,
  };
}

export const chipStyle = estiloChip;

export function Chip({ texto, color, className = '' }: { texto: string; color: string | null; className?: string }) {
  return (
    <span
      className={`inline-block max-w-full truncate rounded-full border border-border px-2.5 py-0.5 text-xs font-bold ${className}`}
      style={estiloChip(color)}
    >
      {texto}
    </span>
  );
}

export interface ChipSelectProps {
  /** Clave estable de la fuente (ej. 'tipificacion', 'opciones_pago'), para disparar refetch y guardar dimensiones. */
  claveFuente?: string;
  sourceKey?: string;
  fuente: FuenteChips;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  canEdit?: boolean;
  puedeEditar?: boolean;
  className?: string;
  disabled?: boolean;
}

export function ChipSelect({
  claveFuente,
  sourceKey,
  fuente,
  label,
  value,
  onChange,
  placeholder,
  canEdit,
  puedeEditar,
  className = '',
  disabled = false,
}: ChipSelectProps) {
  const resolvedKey = claveFuente ?? sourceKey ?? label.toLowerCase().replace(/\s+/g, '-');
  const { t } = useI18n();
  const org = useOrg();
  const defaultCanEdit = org.status === 'ready' && !org.isViewingAs && isAdmin(org.role);
  const allowEdit = canEdit ?? puedeEditar ?? defaultCanEdit;

  const defaultPlaceholder = placeholder ?? t('grid.smartchipEmptyValue');

  const [items, setItems] = useState<ItemChip[]>([]);
  const [abierto, setAbierto] = useState(false);
  const [editando, setEditando] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [abrirHaciaArriba, setAbrirHaciaArriba] = useState(false);
  const contenedorRef = useRef<HTMLLabelElement>(null);
  const buscadorRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const { panelRef: listaRef, tamano, empezarResize } = useResizableSize(
    null,
    { key: `numtable-chipselect-${resolvedKey}-size`, minWidth: 200, minHeight: 140 },
  );

  const cargar = async () => {
    try {
      setItems(await fuente.cargar());
      setError(null);
    } catch (cargaError) {
      setError((cargaError as Error).message);
    }
  };

  useEffect(() => {
    void cargar();
  }, [resolvedKey, fuente]);

  useEffect(() => {
    if (!abierto) return;
    buscadorRef.current?.focus();
    const onClickFuera = (e: MouseEvent) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target as Node)) {
        setAbierto(false);
        setEditando(false);
        setFiltro('');
      }
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  useLayoutEffect(() => {
    if (!abierto || !contenedorRef.current || !panelRef.current) return;
    const rectBoton = contenedorRef.current.getBoundingClientRect();
    const altoPanel = panelRef.current.offsetHeight;
    const espacioAbajo = window.innerHeight - rectBoton.bottom;
    setAbrirHaciaArriba(espacioAbajo < altoPanel && rectBoton.top > espacioAbajo);
  }, [abierto, editando, items.length, filtro]);

  const seleccionado = items.find((i) => i.clave === value);

  const filtrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.texto.toLowerCase().includes(q));
  }, [filtro, items]);

  const elegir = (v: string) => {
    onChange(v);
    setAbierto(false);
    setEditando(false);
    setFiltro('');
  };

  const navegables = useMemo(() => ['', ...filtrados.map((i) => i.clave)], [filtrados]);
  const { indice, setIndice, refActiva, alTeclear } = useDropdownKeyboard({
    cantidad: navegables.length,
    abierto,
    indiceInicial: filtro.trim()
      ? (filtrados.length > 0 ? 1 : 0)
      : Math.max(0, filtrados.findIndex((i) => i.clave === value) + 1),
    reiniciarCon: filtro,
    elegir: (i) => elegir(navegables[i] ?? ''),
    cerrar: () => {
      setAbierto(false);
      setEditando(false);
      setFiltro('');
    },
  });

  return (
    <label className={`relative block text-sm font-bold ${className}`} ref={contenedorRef}>
      {label}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setAbierto((a) => !a)}
        className="mt-1 flex h-10 w-full items-center justify-between rounded-lg border border-border bg-surface px-3 text-left font-normal transition-colors hover:border-accent disabled:cursor-not-allowed disabled:opacity-50"
      >
        {seleccionado ? (
          <Chip texto={seleccionado.texto} color={seleccionado.color} />
        ) : value ? (
          <span className="truncate">{value}</span>
        ) : (
          <span className="truncate text-muted">{defaultPlaceholder}</span>
        )}
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" className="ml-1 shrink-0 text-muted">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {abierto && (
        <div
          ref={panelRef}
          className={`absolute left-0 z-40 flex gap-2 ${abrirHaciaArriba ? 'bottom-full mb-1' : 'top-full mt-1'}`}
        >
          <div
            ref={listaRef}
            style={tamano ? { width: tamano.width, height: tamano.height } : undefined}
            className={`relative flex flex-col rounded-xl border border-border bg-surface shadow-card ${tamano ? '' : 'w-64'}`}
          >
            <input
              ref={buscadorRef}
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              onKeyDown={alTeclear}
              placeholder={t('chipSelect.searchPlaceholder')}
              className="w-full shrink-0 border-b border-border bg-transparent px-3 py-2 text-sm font-normal text-text outline-none focus:border-accent"
            />
            <div className={`min-h-0 flex-1 overflow-y-auto py-1 ${tamano ? '' : 'max-h-56'}`}>
              <button
                type="button"
                ref={indice === 0 ? refActiva : undefined}
                onClick={() => elegir('')}
                onMouseEnter={() => setIndice(0)}
                className={`block w-full px-3 py-1.5 text-left text-sm font-normal text-muted hover:bg-surface-3 ${indice === 0 ? 'bg-surface-3' : ''}`}
              >
                {defaultPlaceholder}
              </button>
              {filtrados.length === 0 && (
                <p className="px-3 py-1.5 text-sm font-normal text-muted">{t('chipSelect.noMatches')}</p>
              )}
              {filtrados.map((item, i) => (
                <button
                  key={item.clave}
                  type="button"
                  ref={indice === i + 1 ? refActiva : undefined}
                  onClick={() => elegir(item.clave)}
                  onMouseEnter={() => setIndice(i + 1)}
                  className={`block w-full px-3 py-1.5 text-left hover:bg-surface-3 ${indice === i + 1 ? 'bg-surface-3' : ''}`}
                >
                  <Chip texto={item.texto} color={item.color} />
                </button>
              ))}
            </div>

            {/* Panel footer: edit pencil and resize handle */}
            <div className="flex shrink-0 items-center justify-end gap-1 px-1 pb-1">
              {allowEdit && (
                <button
                  type="button"
                  title={t('chipSelect.editValues')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditando((v) => !v);
                  }}
                  className={`flex h-6 w-6 items-center justify-center rounded hover:bg-surface-3 ${editando ? 'bg-accent-soft text-accent' : 'text-muted'}`}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                </button>
              )}
              <span className="block w-4">
                <ResizeCorner onMouseDown={empezarResize} />
              </span>
            </div>
          </div>

          {editando && allowEdit && (
            <ChipListEditor
              items={items}
              fuente={fuente}
              title={t('chipSelect.editTitle', { name: label.toLowerCase() })}
              value={value}
              onChange={onChange}
              onItemsChange={setItems}
              onReload={() => void cargar()}
              onError={setError}
              className="w-72 rounded-xl border border-border bg-surface p-3 shadow-card"
            />
          )}
        </div>
      )}
      {error && <p className="mt-1 text-xs font-normal text-danger">{error}</p>}
    </label>
  );
}

export default ChipSelect;
